import keenConfig from 'configs/keen-slider'
import { useKeenSlider } from 'keen-slider/react'

import cn from 'classnames'

import Image from 'next/image'
import BigNumber from 'bignumber.js'

import { useTranslation } from 'utils/use-translation'
import { useEffect, useRef, useState } from 'react'

import SelectedIcon from './icons/selected.png'
import styles from './styles.module.scss'
import Loading from './components/loading'

const AprOptions = ({
  data,
  activeOption,
  handleChangeOption,
  containerClassName = '',
  className = '',
  tabClassName = '',
  isSwipable = false,
  isLk = false,
  discount = '',
  ltvPercent = '',
}) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [shouldSwipe, setShouldSwipe] = useState(false)

  const [sliderRef] = useKeenSlider<HTMLDivElement>(
    isSwipable ? keenConfig(8, false, undefined, undefined, false) : undefined,
    [],
  )

  useEffect(() => {
    if (!isSwipable || !containerRef.current || !wrapperRef.current) {
      setShouldSwipe(false)
      return undefined
    }

    const checkSize = () => {
      if (!containerRef.current || !wrapperRef.current) return
      const needSwipe = wrapperRef.current.scrollWidth > containerRef.current.clientWidth
      setShouldSwipe(needSwipe)
    }

    checkSize()

    const ro = new ResizeObserver(checkSize)
    ro.observe(containerRef.current)
    ro.observe(wrapperRef.current)

    return () => {
      ro.disconnect()
    }
  }, [isSwipable, data?.length])

  const getPercentWithoutDiscount = (percent, discount) => {
    if (isNaN(percent) || isNaN(discount) || !percent || !discount) return ''

    try {
      return new BigNumber(percent).dividedBy(ltvPercent).minus(discount).multipliedBy(ltvPercent).toString()
    } catch (error) {
      return ''
    }
  }

  if (data?.length === 0) return <Loading />

  return (
    <div
      ref={containerRef}
      className={cn(styles.container, containerClassName, {
        [styles.slider]: isSwipable,
        [styles.gap]: !shouldSwipe,
      })}
    >
      <div
        className={cn(
          styles.wrapper,
          { 'keen-slider': data.length > 2, [styles.fullwidth]: data.length === 2 },
          className,
        )}
        ref={shouldSwipe ? sliderRef : wrapperRef}
      >
        {data?.map((option) => (
          <div
            key={option?.id}
            className={cn(
              styles.option,
              tabClassName,
              { [styles.fullwidth]: data.length === 2 },
              { 'keen-slider__slide': data.length > 2 },
              {
                [styles.selected]: activeOption === option?.id,
              },
            )}
            onClick={() => handleChangeOption(option?.id)}
          >
            <div className={cn(styles['option-inner'], { [styles.fullwidth]: data.length === 2 })}>
              <p>
                {t(option?.title)}

                <Image className={styles.icon} src={SelectedIcon} width={20} height={20} />
              </p>
              {data.length === 2 ? (
                <div className={styles.apr__data}>
                  <p>
                    <span>{t(option?.apr)}</span>{' '}
                    {discount && (
                      <span className={styles.crossed}>{getPercentWithoutDiscount(option?.percent, discount)}% </span>
                    )}
                    <span className={styles.percent}>{option?.percent}%</span>
                  </p>
                  <p>{t(option?.duration)}</p>
                </div>
              ) : (
                <>
                  <p>
                    <span>{t(option?.apr)}</span>{' '}
                    {discount && (
                      <span className={styles.crossed}>{getPercentWithoutDiscount(option?.percent, discount)}% </span>
                    )}
                    <span className={styles.percent}>{option?.percent}%</span>
                  </p>
                  <p>{t(option?.duration)}</p>
                </>
              )}
            </div>
          </div>
        ))}
        {isSwipable && data.length > 2 && (
          <div key="dummy" className={cn('keen-slider__slide', styles.dummy)} aria-hidden="true" />
        )}
      </div>
    </div>
  )
}

export default AprOptions
